<template>
  <transition
    enter-active-class="slideBounceFadeDown"
    leave-active-class="slideBounceFadeUp"
  >
    <div
      v-if="isShowing"
      ref="hud"
      class="question-mark-block-hud container text-center"
      tabindex="-1"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-describedby="question-mark-block-hud-instructions"
      style="position: fixed; inset: 0 0 auto 0; z-index: var(--depth-foreground); font-size: var(--size-reset-base)"
    >
      <p
        id="question-mark-block-hud-instructions"
        class="sr-only"
      >
        Game <abbr title="Heads Up Display">H.U.D</abbr> Updated: Here's your current coin count, lives, and time.
      </p>

      <div
        class="hud-bar position-relative inverted display-inline-flex flex-items-center flex-content-center padding-top-lg padding-bottom-md padding-x-2xl position-relative with-shadow size-sm"
        style="
          --with-shadow-box-shadow: 0 var(--with-shadow-box-shadow-width) 0 var(--fa-dk-navy);
          border-end-start-radius: var(--border-radius-lg);
          border-end-end-radius: var(--border-radius-lg);
          background-color: var(--fa-navy);
          gap: var(--spacing-6xl);
        "
      >
        <div
          class="display-flex flex-row flex-items-center"
          style="gap: var(--spacing-sm)"
        >
          <font-awesome-icon
            :icon="$v7.duotone[selectedUserIcon]"
            size="xl"
            swap-opacity
            style="--fa-primary-color: var(--fa-teal); --fa-secondary-opacity: 1"
          />
          <font-awesome-icon
            :icon="$v7.solid.faXmark"
            size="sm"
            style="color: var(--fa-dk-gravy)"
          />
          <span class="sr-only">Current Lives: </span>
          <span style="font-family: 'Press Start'; letter-spacing: var(--letter-spacing-xl)">{{ oneUpCount }}</span>
        </div>

        <div
          class="display-flex flex-row flex-items-center flex-content-center"
          style="gap: var(--spacing-sm)"
        >
          <font-awesome-icon
            :icon="$v7.duotone.faCoinFront"
            size="xl"
            style="--fa-secondary-color: var(--fa-yellow); --fa-secondary-opacity: 1"
          />
          <font-awesome-icon
            :icon="$v7.solid.faXmark"
            size="sm"
            style="color: var(--fa-dk-gravy)"
          />
          <span class="sr-only">Current Coins: </span>
          <span style="font-family: 'Press Start'; letter-spacing: var(--letter-spacing-xl)">{{ formattedGlobalBopCount }}</span>
        </div>

        <div
          class="display-flex flex-row flex-items-center flex-content-center"
          style="gap: var(--spacing-sm); font-family: 'Press Start'; letter-spacing: var(--letter-spacing-xl)"
        >
          <font-awesome-icon
            :icon="$v7.duotone.faFontAwesome"
            size="xl"
            style="--fa-secondary-color: var(--fa-brand-blue); --fa-secondary-opacity: 1"
          />
          <font-awesome-icon
            :icon="$v7.solid.faXmark"
            size="sm"
            style="color: var(--fa-dk-gravy)"
          />
          <span class="sr-only">Current Font Awesome Version/World: </span>
          <span style="font-family: 'Press Start'; letter-spacing: var(--letter-spacing-xl)"
            >{{ $releases.active.versionParts.major }}-{{ $releases.active.versionParts.minor }}</span
          >
        </div>

        <div
          class="display-none tablet:display-flex flex-row flex-items-center flex-content-end"
          style="gap: var(--spacing-sm)"
        >
          <font-awesome-icon
            :icon="$v7.duotone.faClock"
            size="xl"
            style="--fa-secondary-color: var(--fa-red); --fa-secondary-opacity: 1"
          />
          <font-awesome-icon
            :icon="$v7.solid.faXmark"
            size="sm"
            style="color: var(--fa-dk-gravy)"
          />
          <span class="sr-only">Current Time: </span>
          <time
            :datetime="isoTime"
            aria-live="polite"
            style="font-family: 'Press Start'; letter-spacing: var(--letter-spacing-xl)"
            >{{ formattedTime }}</time
          >
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
  import { padStart, toString, sample, debounce } from 'lodash'
  import moment from 'moment'

  const userIcons = [
    'faUser',
    'faUserAstronaut',
    'faUserCowboy',
    'faUserCrown',
    'faUserHairMullet',
    'faUserHelmetSafety',
    'faUserNinja',
    'faUserRobot',
    'faUserVisor'
  ]

  export default {
    name: 'Hud',

    props: {
      globalBopCount: Number,
      isShowing: Boolean,
      oneUpCount: Number
    },

    data() {
      return {
        localTime: moment(),
        selectedUserIcon: sample(userIcons)
      }
    },

    computed: {
      formattedTime() {
        return this.localTime.format('hmm')
      },

      formattedGlobalBopCount() {
        return padStart(toString(this.globalBopCount % 100), 2, '0')
      },

      isoTime() {
        return this.localTime.toISOString()
      }
    },

    watch: {
      isShowing(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            const el = this.$refs.hud
            if (el && typeof el.focus === 'function') {
              el.focus()
            }
          })
        }
      }
    },

    mounted() {
      this.updateTime()
      this.timer = setInterval(this.updateTime, 1000)
    },

    beforeDestroy() {
      clearInterval(this.timer)
    },

    methods: {
      updateTime: debounce(function () {
        this.localTime = moment()
      }, 6000)
    }
  }
</script>
